import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Organisation, PROJECT_NAME_MAX } from '../common/organisation';
import { ScenarioMetrics } from './scenario-comparison';
import { SCENARIO_DAYS, ScenarioResult, ScenarioService, ScenarioStep } from './scenario.service';
import { formatWorkingTime, Suggestion } from './simulation-report';

export interface ScenarioDialogData {
    organisation: Organisation;
    suggestions: Suggestion[];          // those with a change to try
    modelKeys: string[];                // the models simulated, as the analysis page shows them
    formatCost: (value: number) => string;
    chooseAll?: boolean;                // all are chosen to start with, rather than the first
}

// what the dialog closes with: the project kept, if it was
export interface ScenarioDialogResult {
    keptProjectKey?: string;
}

const STEP_LABELS: Record<ScenarioStep, string> = {
    creating: 'Creating a project',
    applying: 'Making the changes in it',
    baseline: 'Working out the current state the same way, to compare with',
    current: 'Simulating the current state',
    draft: 'Simulating the draft',
};

const METRICS: { key: keyof ScenarioMetrics; label: string; lowerIsBetter: boolean; format: 'number' | 'time' | 'cost' }[] = [
    { key: 'produced', label: 'Outputs produced', lowerIsBetter: false, format: 'number' },
    { key: 'waiting', label: 'Items waiting at steps', lowerIsBetter: true, format: 'number' },
    { key: 'delaySeconds', label: 'Average wait on the way to an output', lowerIsBetter: true, format: 'time' },
    { key: 'behind', label: "Steps that can't keep up", lowerIsBetter: true, format: 'number' },
    { key: 'outOfStep', label: 'Steps waiting for their least input', lowerIsBetter: true, format: 'number' },
    { key: 'cost', label: 'Annual cost of people and systems', lowerIsBetter: true, format: 'cost' },
];

// tries suggestions from a simulation in a new project, and compares a simulation of it with the current state, so
// the user can keep the project or delete it
@Component({
    selector: 'app-scenario-dialog',
    standalone: true,
    imports: [FormsModule, MatButtonModule, MatCheckboxModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressBarModule],
    template: `
        <h2 mat-dialog-title>Test changes in a project</h2>
        <mat-dialog-content>
            @switch (stage) {
            @case ('choose') {
            <p>Choose the changes to try. They're made in a new project, and the current state and the project are each
                simulated for {{ days }} working days so you can compare them.</p>
            <ul class="choices">
                @for (s of data.suggestions; track $index) {
                <li><mat-checkbox [(ngModel)]="chosen[$index]" [attr.data-testid]="'scenario-choice-' + $index">{{ s.text }}</mat-checkbox>
                    <span class="change">{{ s.change!.description }}</span></li>
                }
            </ul>
            }
            @case ('working') {
            <p role="status" data-testid="scenario-progress">{{ stepLabel }}…</p>
            <mat-progress-bar [mode]="share === undefined ? 'indeterminate' : 'determinate'" [value]="(share || 0) * 100"></mat-progress-bar>
            }
            @case ('error') {
            <p class="error" role="alert">{{ error }}</p>
            }
            @case ('results') {
            @if (result; as r) {
            <p class="verdict" [class]="'verdict ' + r.comparison.verdict" data-testid="scenario-verdict">{{ r.comparison.summary }}</p>

            <h4>Changes made in “{{ r.project.name }}”</h4>
            <ul class="made">
                @for (c of r.changes; track $index) {
                <li><mat-icon [class.failed]="!c.applied">{{ c.applied ? 'check' : 'close' }}</mat-icon>{{ c.description }}@if (!c.applied && c.reason) {<span class="reason"> ({{ c.reason }})</span>}</li>
                }
            </ul>

            <h4>Over {{ days }} working days</h4>
            <table data-testid="scenario-metrics">
                <thead><tr><th></th><th class="num">Current state</th><th class="num">With the changes</th><th class="num">Change</th></tr></thead>
                <tbody>
                    @for (m of metrics; track m.key) {
                    <tr>
                        <td>{{ m.label }}</td>
                        <td class="num">{{ show(m.format, r.comparison.current[m.key]) }}</td>
                        <td class="num">{{ show(m.format, r.comparison.draft[m.key]) }}</td>
                        <td class="num" [class.better]="isBetter(m, r)" [class.worse]="isWorse(m, r)">{{ difference(m, r) }}</td>
                    </tr>
                    }
                </tbody>
            </table>

            @if (r.comparison.steps.length) {
            <h4>Where work waited</h4>
            <table data-testid="scenario-steps">
                <thead><tr><th>Step</th><th class="num">Current wait</th><th class="num">Wait with the changes</th></tr></thead>
                <tbody>
                    @for (s of r.comparison.steps.slice(0, 12); track s.key) {
                    <tr>
                        <td>{{ s.name }}<span class="model">{{ s.model }}</span></td>
                        <td class="num">{{ wait(s.current.waitSeconds, s.current.queue) }}</td>
                        <td class="num" [class.better]="s.better" [class.worse]="!s.better && s.draft && s.draft.waitSeconds > s.current.waitSeconds">
                            {{ s.draft ? wait(s.draft.waitSeconds, s.draft.queue) : 'no longer a step' }}</td>
                    </tr>
                    }
                </tbody>
            </table>
            }
            <p class="note">A simulation shows how work flows with the model's durations, volumes and capacities; check those
                for the steps involved before acting on it.</p>

            <mat-form-field class="name" appearance="outline">
                <mat-label>Project name, to keep it</mat-label>
                <input matInput [(ngModel)]="name" [attr.maxlength]="nameMax" data-testid="scenario-name">
            </mat-form-field>
            }
            }
            }
        </mat-dialog-content>
        <mat-dialog-actions align="end">
            @switch (stage) {
            @case ('choose') {
            <button mat-button mat-dialog-close>Cancel</button>
            <button mat-flat-button color="primary" [disabled]="!anyChosen" (click)="test()" data-testid="scenario-test">Test in a project</button>
            }
            @case ('error') {
            <button mat-button mat-dialog-close>Close</button>
            <button mat-flat-button color="primary" (click)="stage = 'choose'">Back</button>
            }
            @case ('results') {
            <button mat-button (click)="discard()" [disabled]="busy" data-testid="scenario-discard">Delete the project</button>
            <button mat-flat-button color="primary" (click)="keep()" [disabled]="busy || !name.trim()" data-testid="scenario-keep">Keep the project</button>
            }
            }
        </mat-dialog-actions>
    `,
    styles: [`
        .choices, .made { list-style: none; margin: 0; padding: 0; }
        .choices li { padding: 6px 0; border-top: 1px solid rgba(0,0,0,.08); }
        .choices .change { display: block; margin-left: 40px; color: var(--slate, #5b6172); font-size: 12px; }
        .made li { display: flex; align-items: flex-start; gap: 6px; padding: 2px 0; }
        .made mat-icon { color: #15803d; font-size: 18px; width: 18px; height: 18px; flex: none; }
        .made mat-icon.failed { color: #b91c1c; }
        .reason, .model, .note { color: var(--slate, #5b6172); }
        .model { display: block; font-size: 12px; }
        .note { font-size: 12px; }
        h4 { margin: 14px 0 4px; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; font-weight: 600; font-size: 12px; color: var(--slate, #5b6172); padding: 2px 6px; }
        td { padding: 4px 6px; border-top: 1px solid rgba(0,0,0,.08); vertical-align: top; overflow-wrap: anywhere; }
        .num { text-align: right; white-space: nowrap; }
        .better { color: #15803d; font-weight: 600; }
        .worse { color: #b91c1c; font-weight: 600; }
        .verdict { padding: 8px 12px; border-radius: 8px; font-weight: 500; }
        .verdict.better { background: #dcfce7; color: #166534; }
        .verdict.mixed { background: #fef3c7; color: #92400e; }
        .verdict.worse { background: #fee2e2; color: #991b1b; }
        .verdict.same { background: #e5e7eb; color: #374151; }
        .error { color: #b91c1c; }
        .name { width: 100%; margin-top: 12px; }
    `],
})
export class ScenarioDialogComponent {
    readonly days = SCENARIO_DAYS;
    readonly metrics = METRICS;
    readonly nameMax = PROJECT_NAME_MAX;

    stage: 'choose' | 'working' | 'error' | 'results' = 'choose';
    chosen: boolean[];
    stepLabel = '';
    share?: number;
    error = '';
    result?: ScenarioResult;
    name = '';
    busy = false;

    constructor(@Inject(MAT_DIALOG_DATA) public data: ScenarioDialogData, private dialogRef: MatDialogRef<ScenarioDialogComponent, ScenarioDialogResult>,
        private scenarios: ScenarioService) {
        // the first is chosen to start with, as it's the most pressing, unless they were chosen already
        this.chosen = data.suggestions.map((_, i) => !!data.chooseAll || i === 0);
    }

    get anyChosen() {
        return this.chosen.some(c => c);
    }

    async test() {
        const suggestions = this.data.suggestions.filter((_, i) => this.chosen[i]);
        const changes = suggestions.map(s => s.change!);
        this.stage = 'working';
        // the project is the user's to keep or delete, so it can't be closed by clicking away
        this.dialogRef.disableClose = true;
        try {
            const name = changes.length === 1 ? changes[0].description : `${changes[0].description} +${changes.length - 1}`;
            this.result = await this.scenarios.test(this.data.organisation, changes, this.data.modelKeys, name, (step, share) => {
                this.stepLabel = STEP_LABELS[step];
                this.share = share;
            }, this.data.formatCost);
            this.name = this.result.project.name;
            this.stage = 'results';
        }catch(err: any) {
            console.error('Unable to test the changes', err);
            this.error = err?.message || "The changes couldn't be tested. Please try again.";
            this.stage = 'error';
            this.dialogRef.disableClose = false;
        }
    }

    async discard() {
        if (!this.result) return;
        this.busy = true;
        try {
            await this.scenarios.discard(this.result.project);
            this.dialogRef.close({});
        }finally {
            this.busy = false;
        }
    }

    async keep() {
        if (!this.result || !this.name.trim()) return;
        this.busy = true;
        try {
            await this.scenarios.keep(this.result.project, this.name.trim().slice(0, PROJECT_NAME_MAX));
            this.dialogRef.close({ keptProjectKey: this.result.project._key });
        }finally {
            this.busy = false;
        }
    }

    show(format: 'number' | 'time' | 'cost', value: number) {
        if (format === 'time') return value ? formatWorkingTime(value) : '–';
        if (format === 'cost') return this.data.formatCost(value);
        return Math.round(value).toLocaleString();
    }

    wait(seconds: number, queue: number) {
        return queue === 0 ? 'none' : `${formatWorkingTime(seconds)} (${queue.toLocaleString()} waiting)`;
    }

    private delta(m: typeof METRICS[number], r: ScenarioResult) {
        return r.comparison.draft[m.key] - r.comparison.current[m.key];
    }

    isBetter(m: typeof METRICS[number], r: ScenarioResult) {
        const d = this.delta(m, r);
        return m.lowerIsBetter ? d < 0 : d > 0;
    }

    isWorse(m: typeof METRICS[number], r: ScenarioResult) {
        const d = this.delta(m, r);
        return m.lowerIsBetter ? d > 0 : d < 0;
    }

    difference(m: typeof METRICS[number], r: ScenarioResult) {
        const d = this.delta(m, r);
        if (!d) return '–';
        const sign = d > 0 ? '+' : '−';
        if (m.format === 'time') return `${sign}${formatWorkingTime(Math.abs(d))}`;
        if (m.format === 'cost') return `${sign}${this.data.formatCost(Math.abs(d))}`;
        const current = r.comparison.current[m.key];
        return `${sign}${Math.round(Math.abs(d)).toLocaleString()}${current ? ` (${Math.round(Math.abs(d)/current*100)}%)` : ''}`;
    }
}
